import { getAdobeAccessToken } from '../auth/adobeIms';
import { AI_SYSTEM_CONTEXT } from './aiContext';

const DB_ACTION_BASE_URL = 'https://55338-809mauveporcupine.adobeioruntime.net/api/v1/web/ondemand-dashboard';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface SseEvent {
  event: string;
  data: string;
}

function actionUrl(action: string): string {
  return `${DB_ACTION_BASE_URL.replace(/\/$/, '')}/${action}`;
}

function getAuthHeaders(): Record<string, string> {
  const token = getAdobeAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function getPageContext() {
  return { path: '/home/workspaces', object_details: null };
}

/**
 * Call the AI agent proxy action. Returns parsed SSE events as an array.
 */
async function callAiProxy(
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<SseEvent[]> {
  const token = getAdobeAccessToken();
  const resp = await fetch(actionUrl('ai-agent-proxy'), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      path,
      body,
      aiToken: token,
    }),
    signal,
  });

  if (!resp.ok) throw new Error(`AI proxy failed: HTTP ${resp.status}`);

  const json = await resp.json();
  const data = json.body || json;
  return Array.isArray(data.events) ? data.events : [];
}

/**
 * Initiate a new conversation. Returns parsed SSE events and the conversation_id.
 */
export async function initiateConversation(
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
): Promise<string | null> {
  const events = await callAiProxy(
    'conversations?channel=stream&routing=router',
    {
      page_context: getPageContext(),
      query: AI_SYSTEM_CONTEXT,
      application: 'workfront',
    },
    signal,
  );

  let conversationId: string | null = null;

  for (const evt of events) {
    if (evt.event === 'init') {
      try {
        const parsed = JSON.parse(evt.data);
        conversationId = parsed.conversation_id || null;
      } catch { /* ignore */ }
    }
    onEvent(evt);
  }

  return conversationId;
}

/**
 * Send a message in an existing conversation. Returns parsed SSE events.
 */
export async function sendMessage(
  conversationId: string,
  query: string,
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const events = await callAiProxy(
    `conversations/${conversationId}?channel=stream&routing=router`,
    {
      page_context: getPageContext(),
      query: `${AI_SYSTEM_CONTEXT}\n\nUser query: ${query}`,
      application: 'workfront',
    },
    signal,
  );

  for (const evt of events) {
    onEvent(evt);
  }
}

// ─── localStorage persistence ─────────────────────────────────────────────

const STORAGE_KEY = 'ai-chat-history';

export function loadChatHistory(): { conversationId: string | null; messages: ChatMessage[] } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { conversationId: null, messages: [] };
    const parsed = JSON.parse(raw);
    return {
      conversationId: parsed.conversationId || null,
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
    };
  } catch {
    return { conversationId: null, messages: [] };
  }
}

export function saveChatHistory(conversationId: string | null, messages: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ conversationId, messages }));
  } catch { /* quota exceeded — ignore */ }
}

export function clearChatHistory(): void {
  localStorage.removeItem(STORAGE_KEY);
}
